import React from 'react';
import Select from 'react-select';
import FontAwesomeIcon from 'src/components/Icon';
import Button from 'src/components/Button';
import SelectedContactCard from 'src/pages/ServiceRequest/ContactPicker/components/SelectedContactCard';
import { variables } from 'src/Tenants';
import {
  IStrataManager,
  IUnitStrataAssignments,
} from 'src/pages/Property/ManageStrataManagers/types';
import { getAssignableManagerOptions } from 'src/pages/Property/ManageStrataManagers/helpers';
import {
  useSuiteManagerCardOrchestrator,
  UseSuiteManagerCardOrchestratorReturn,
} from './useSuiteManagerCardOrchestrator';
import './suite-manager-card.scss';

interface SuiteManagerCardProps {
  unit: IUnitStrataAssignments;
  availableManagers: IStrataManager[];
}

interface ManagerOption {
  value: string;
  label: string;
}

const managerFullName = (manager: IStrataManager): string =>
  `${manager.firstName} ${manager.lastName}`.trim();

const SuiteManagerCard = ({
  unit,
  availableManagers,
}: SuiteManagerCardProps) => {
  const {
    assignedManagers,
    isDirty,
    isSaving,
    addManagers,
    removeManager,
    save,
  }: UseSuiteManagerCardOrchestratorReturn = useSuiteManagerCardOrchestrator({
    unit,
  });

  const assignableOptions: ManagerOption[] = getAssignableManagerOptions(
    assignedManagers,
    availableManagers
  ).map((manager: IStrataManager) => ({
    value: manager.userId,
    label: managerFullName(manager),
  }));

  const handleSelectManager = (option: ManagerOption | null): void => {
    if (!option) {
      return;
    }

    const manager: IStrataManager | undefined = availableManagers.find(
      (m: IStrataManager) => m.userId === option.value
    );

    if (!manager) {
      return;
    }

    addManagers([manager]);
  };

  const hasManagers: boolean = assignedManagers.length > 0;

  return (
    <div className="suite-manager-card">
      <div className="suite-manager-card-header">
        <span className="suite-manager-card-unit-number">
          {unit.unitNumber}
        </span>
        {unit.building && (
          <span className="suite-manager-card-building">{unit.building}</span>
        )}
      </div>

      <div className="suite-manager-card-managers">
        {hasManagers ? (
          assignedManagers.map((manager: IStrataManager) => (
            <div key={manager.userId} className="suite-manager-card-row">
              <SelectedContactCard
                name={managerFullName(manager)}
                role={variables.strataManager}
                profileImage={manager.profileImage}
                badge="property-manager"
              />
              <button
                type="button"
                className="suite-manager-card-remove"
                title="Remove"
                aria-label={`Remove ${managerFullName(manager)}`}
                onClick={() => removeManager(manager.userId)}
              >
                <FontAwesomeIcon glyph="times" weight="light" />
              </button>
            </div>
          ))
        ) : (
          <div className="suite-manager-card-empty">
            No {variables.strataManagers} assigned yet.
          </div>
        )}
      </div>

      <div className="suite-manager-card-footer">
        <div className="suite-manager-card-select-wrap">
          <Select
            classNamePrefix="suite-manager-card-select"
            options={assignableOptions}
            value={null}
            onChange={(option: any) => handleSelectManager(option)}
            placeholder={`Add a ${variables.strataManager}…`}
            isDisabled={assignableOptions.length === 0 || isSaving}
            noOptionsMessage={() => `No more ${variables.strataManagers} to add`}
          />
        </div>
        <Button
          color="teal"
          size="small"
          onClick={() => save()}
          disabled={!isDirty || isSaving}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default SuiteManagerCard;